/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState } from 'react';
import { FrMonth } from 'cpfr-common';
import { Combobox, ErrorMessage, FormField } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import { ComboboxOption } from '../../../../../../../types/bronson-react';

type MonthSelectionProps = {
    label: string;
    name: string;
    maxSelections: number;
    isMandatory?: boolean;
};

type ComboboxItem = {
    key: string;
    value: string;
    disabled: boolean;
};

export const DeadlineMonthsSelection: React.FC<MonthSelectionProps> = ({
    label,
    name,
    maxSelections,
    isMandatory = true,
}) => {
    const [field, meta, helpers] = useField(name);
    const formikValue: string[] = field.value || [];
    const [selectedItem, setSelectedItem] = useState<{ value: ComboboxItem[] }>({
        value: formikValue.map((v: string) => ({ key: v, value: v, disabled: false })),
    });
    const onComboboxChange = useCallback(
        (values: any) => {
            const limitedValues = values.slice(0, maxSelections);
            setSelectedItem((prev) => ({
                ...prev,
                value: limitedValues,
            }));
            helpers.setValue(limitedValues.map((value: ComboboxOption) => value.value));
        },
        [maxSelections],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    const months: FrMonth[] = [
        'Janvier',
        'Février',
        'Mars',
        'Avril',
        'Mai',
        'Juin',
        'Juillet',
        'Août',
        'Septembre',
        'Octobre',
        'Novembre',
        'Décembre',
    ];
    const currentDate = new Date();
    const currentMonthIndex = currentDate.getMonth();
    const currentYearMonthsToDisplay =
        currentMonthIndex === 11 ? [] : months.slice(currentMonthIndex + 1, months.length);

    const areAllOptionsSelected = field.value?.length >= maxSelections;

    const getOptionValue = (month: FrMonth, yearOffset = 0): string => {
        return `${month} ${currentDate.getFullYear() + yearOffset}`;
    };

    const selectItems: ComboboxItem[] = currentYearMonthsToDisplay.length
        ? currentYearMonthsToDisplay.map((month: FrMonth) => {
              const option = getOptionValue(month);
              return {
                  key: option,
                  value: option,
                  disabled: areAllOptionsSelected && !field.value.includes(option),
              };
          })
        : [];

    const nextYearMonthsToDisplay = months.slice(0, months.length - currentYearMonthsToDisplay.length);
    nextYearMonthsToDisplay.forEach((month: FrMonth) => {
        const option = getOptionValue(month, 1);
        selectItems.push({
            key: option,
            value: option,
            disabled: areAllOptionsSelected && !field.value.includes(option),
        });
    });

    return (
        <FormField
            type="select"
            testId="deadline-months-selection"
            id="deadline-months-selection"
            labelText={isMandatory ? `${label} *` : label}
            errorMessage={
                meta.touched &&
                meta.error && <ErrorMessage testId="deadline-months-error-message">{meta.error}</ErrorMessage>
            }
            noFloatingLabel
        >
            <Combobox
                name={name}
                required={isMandatory}
                aria-required={isMandatory}
                onBlur={() => helpers.setTouched(true, true)}
                testId={name}
                multiple={true}
                searchable={false}
                value={selectedItem.value}
                onChange={onComboboxChange}
                className="c-form-field--no-floating"
                options={selectItems}
            ></Combobox>
        </FormField>
    );
};
